import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useStore } from '@/store'

import { VIEW_BOX, toSVG } from './geo'
import { MeridianLines, TimezoneAxisOverlay } from './TimeZones'
import { card, col, BASE_MAP_CSS } from './styles'
import { PanelHeader } from './PanelHeader'
import { ZoomControls } from './ZoomControls'
import { SvgGradients } from './SvgGradients'
import { WorldPaths } from './WorldPaths'
import { HopMarkers } from './markers/HopMarkers'
import { ClusterMarkers } from './markers/ClusterMarkers'
import { clusterHops } from './clusterHops'
import { PipelineArcs, buildHopPositions, countriesNeededForPlacement, continentsNeededForPlacement } from './PipelineArcs'
import { buildContinentClusterArcs } from './buildContinentClusterArcs'
import { Legend } from './Legend'
import { Tooltip } from './Tooltip'
import { segmentSevForNode, worstTier } from './segments'
import IncidentsPanel from '@/components/views/IncidentsPanel'

import { useCountryStyles } from '@/hooks/useCountryStyles'
import { useCentroids } from '@/hooks/useCentroids'
import { usePanZoom } from '@/hooks/usePanZoom'
import { useHover } from '@/hooks/useHover'
import { useWorldFlowData } from '@/hooks/useWorldFlowData'
import type { WorldPoint } from '@/analysis/apiTypes'
import { REGION_LABEL, CONTINENT_NAMES, countryToContinent } from '@/constants'

function normalizeContinentName(raw: string): string | null {
  const s = raw.trim()
  if (!s) return null
  const up = s.toUpperCase()
  const CONTINENT_CODES = new Set(['EU', 'NA', 'SA', 'AS', 'AF', 'OC', 'OT', 'AN', 'ME'])
  if (CONTINENT_CODES.has(up) && REGION_LABEL[up]) return REGION_LABEL[up]!
  const hit = CONTINENT_NAMES.find((n) => n.toLowerCase() === s.toLowerCase())
  return hit ?? null
}

export default function WorldView() {
  const drillToRegion        = useStore((s) => s.drillToRegion)
  const worldIncidentsOpen   = useStore((s) => s.worldIncidentsOpen)
  const toggleWorldIncidents = useStore((s) => s.toggleWorldIncidents)
  const worldLayers          = useStore((s) => s.worldLayers)
  const worldSegment         = useStore((s) => s.worldSegment)
  const worldClientGrain     = useStore((s) => s.worldClientGrain)
  const dataDebouncing       = useStore((s) => s.dataDebouncing)
  const streamsLoading       = useStore((s) => s.streamsLoading)
  const wsUiPaused           = useStore((s) => s.wsUiPaused)
  const lastDataError        = useStore((s) => s.lastDataError)

  const flow = useWorldFlowData()

  const cityHops = useMemo(() => flow.hops.filter((h) => h.type === 'city'), [flow.hops])

  const countryNodes = useMemo(() => {
    type CountryNode = {
      value: string
      totalStreams: number
      usersTotal: number
      avgSeverity: 0 | 1 | 2 | null
      active: boolean
      metrics: unknown
    }
    const byCc = new Map<string, CountryNode>()

    // 1) Aggregate city hops by ISO2 (hop metrics are the baseline).
    for (const h of cityHops) {
      const cc = (h.country ?? (h.value.includes('|') ? h.value.split('|')[1] : h.value))?.toUpperCase()
      if (!cc || cc.length !== 2) continue
      const sev = segmentSevForNode(h, worldSegment)
      const prev = byCc.get(cc)
      if (!prev) {
        byCc.set(cc, {
          value: cc,
          totalStreams: h.totalStreams,
          usersTotal: h.usersTotal,
          avgSeverity: sev,
          active: h.totalStreams > 0,
          metrics: h.metrics,
        })
      } else {
        prev.totalStreams += h.totalStreams
        prev.usersTotal += h.usersTotal
        prev.avgSeverity = worstTier(prev.avgSeverity, sev)
        prev.active = prev.active || h.totalStreams > 0
      }
    }

    // 2) Overlay regionSeverity severity onto existing hop-backed nodes only.
    const region = flow.regionSeverity
    if (region) {
      for (const [rawCc, avgSeverity] of Object.entries(region)) {
        const cc = String(rawCc ?? '').trim().toUpperCase()
        if (cc.length !== 2) continue
        const sev =
          avgSeverity === 0 || avgSeverity === 1 || avgSeverity === 2 ? avgSeverity : null
        const prev = byCc.get(cc)
        if (!prev || sev == null) continue
        prev.avgSeverity = sev
      }
    }

    return [...byCc.values()]
  }, [cityHops, worldSegment, flow.regionSeverity])

  const continentNodes = useMemo(() => {
    if (worldClientGrain !== 'continent') return []
    // Roll ISO2 country severity up to continents — more reliable than hop continent labels.
    const byCont = new Map<string, {
      value: string
      label: string
      totalStreams: number
      usersTotal: number
      avgSeverity: 0 | 1 | 2 | null
      active: boolean
      metrics: unknown
    }>()
    for (const n of countryNodes) {
      const cc = String(n.value ?? '').trim().toUpperCase()
      if (cc.length !== 2) continue
      const cont = countryToContinent(cc)
      if (!cont || cont === 'Unknown') continue
      const prev = byCont.get(cont)
      const sev = n.avgSeverity === 0 || n.avgSeverity === 1 || n.avgSeverity === 2 ? n.avgSeverity : null
      const active = Number(n.totalStreams ?? 0) > 0
      if (!prev) {
        byCont.set(cont, {
          value: cont,
          label: cont,
          totalStreams: Number(n.totalStreams ?? 0),
          usersTotal: Number(n.usersTotal ?? 0),
          avgSeverity: sev,
          active,
          metrics: n.metrics ?? {},
        })
      } else {
        prev.totalStreams += Number(n.totalStreams ?? 0)
        prev.usersTotal += Number(n.usersTotal ?? 0)
        prev.avgSeverity = worstTier(prev.avgSeverity, sev)
        prev.active = prev.active || active
      }
    }
    if (byCont.size > 0) return [...byCont.values()]

    // Fallback: city hop continent labels when regionSeverity/country nodes are empty.
    return cityHops.flatMap((h) => {
      const name = normalizeContinentName(h.continent ?? '') 
      if (!name) return []
      return [{
        value: name,
        label: name,
        totalStreams: h.totalStreams,
        usersTotal: h.usersTotal,
        avgSeverity: segmentSevForNode(h, worldSegment),
        active: h.totalStreams > 0,
        metrics: h.metrics,
      }]
    })
  }, [countryNodes, cityHops, worldClientGrain, worldSegment])

  const severityFor = useCallback(
    (n: Record<string, unknown>) => {
      const s = n.avgSeverity
      return s === 0 || s === 1 || s === 2 ? s : null
    },
    [],
  )

  const derivedIncidents = useMemo(() => {
    return cityHops
      .filter((h) => h.avgSeverity === 0 || h.avgSeverity === 1)
      .slice(0, 200)
      .map((h, idx) => {
        const sev = (h.avgSeverity ?? 0) as 0 | 1 | 2
        const region = h.country ?? h.value
        return {
          id: idx + 1,
          name: h.label || h.value,
          msg: sev === 0 ? 'Critical hop health' : 'Warning hop health',
          since: '',
          region,
          sev,
        }
      })
  }, [cityHops])

  const openCount = derivedIncidents.length

  const { zoom, pan, isPanning, svgRef, handlers, controls } = usePanZoom()
  const { hover, onEnter, onLeave } = useHover()
  const didFitRef = useRef(false)

  const { countryCss, continentCss } = useCountryStyles({
    dimension: worldClientGrain === 'continent' ? 'continent' : 'country',
    nodes: worldClientGrain === 'continent' ? continentNodes : countryNodes,
  })

  const countryPoints = useMemo((): WorldPoint[] => {
    return countriesNeededForPlacement(flow.hops).map((cc) => ({
      id: cc,
      label: cc,
      lat: 0,
      lon: 0,
      total: 1,
      crit: 0,
      warn: 0,
      sev: 2 as const,
    }))
  }, [flow.hops])

  const countryCentroids = useCentroids({
    svgRef,
    points: countryPoints,
    dimension: 'country',
    agg: null,
  })

  const continentPoints = useMemo((): WorldPoint[] => {
    return continentsNeededForPlacement(flow.hops).map((id) => ({
      id,
      label: id,
      lat: 0,
      lon: 0,
      total: 1,
      crit: 0,
      warn: 0,
      sev: 2 as const,
    }))
  }, [flow.hops])

  const continentCentroids = useCentroids({
    svgRef,
    points: continentPoints,
    dimension: 'continent',
    agg: null,
  })

  const hopPositions = useMemo(
    () => buildHopPositions(flow.hops, countryCentroids, continentCentroids),
    [flow.hops, countryCentroids, continentCentroids],
  )

  const studioClusters = useMemo(
    () => clusterHops({
      hops: flow.hops,
      positions: hopPositions,
      pan,
      zoom,
      segment: worldSegment,
      layers: { studios: worldLayers.studios, edges: false, clients: false },
    }),
    [flow.hops, hopPositions, pan, zoom, worldSegment, worldLayers.studios],
  )

  const clientClusters = useMemo(
    () => clusterHops({
      hops: flow.hops,
      positions: hopPositions,
      pan,
      zoom,
      segment: worldSegment,
      layers: { studios: false, edges: false, clients: worldLayers.clients },
    }),
    [flow.hops, hopPositions, pan, zoom, worldSegment, worldLayers.clients],
  )

  const edgeClusters = useMemo(
    () => clusterHops({
      hops: flow.hops,
      positions: hopPositions,
      pan,
      zoom,
      segment: worldSegment,
      layers: { studios: false, edges: worldLayers.edges, clients: false },
    }),
    [flow.hops, hopPositions, pan, zoom, worldSegment, worldLayers.edges],
  )

  const clusteredHopIds = useMemo(() => {
    const ids = new Set<string>()
    for (const c of [...studioClusters.clusters, ...clientClusters.clusters, ...edgeClusters.clusters]) {
      if (c.members.length < 2) continue
      for (const m of c.members) ids.add(m.hopId)
    }
    return ids
  }, [studioClusters, clientClusters, edgeClusters])

  const visibleLinks = useMemo(() => {
    if (!worldLayers.flows) return []
    if (worldClientGrain === 'continent') return []
    // Prefer partitioned studio→client delivery arcs when present.
    const studioClient = flow.links.filter((l) => {
      if (l.from.type !== 'studio' || l.to.type !== 'city') return false
      if (!worldLayers.studios) return false
      if (!worldLayers.clients) return false
      return true
    })
    if (studioClient.length > 0) return studioClient
    return flow.links.filter((l) => {
      if (l.from.type === 'studio' && !worldLayers.studios) return false
      if (l.to.type === 'city' && !worldLayers.clients) return false
      return true
    })
  }, [flow.links, worldLayers, worldClientGrain])

  const continentClusterArcs = useMemo(() => {
    if (!worldLayers.flows || worldClientGrain !== 'continent') return undefined
    const links = flow.links.filter((l) => {
      if (l.from.type !== 'studio' || l.to.type !== 'city') return false
      if (!worldLayers.studios) return false
      if (!worldLayers.clients) return false
      return true
    })
    return buildContinentClusterArcs(links, flow.hops)
  }, [flow.links, flow.hops, worldLayers, worldClientGrain])

  const mapToScreen = useCallback(
    (x: number, y: number) => ({ x: pan.x + x * zoom, y: pan.y + y * zoom }),
    [pan.x, pan.y, zoom],
  )

  const fitPoints = useMemo(() => {
    const pts = [...Object.values(hopPositions)]
    for (const h of flow.hops) {
      if (h.lat == null || h.lon == null) continue
      pts.push(toSVG(h.lat, h.lon))
    }
    return pts
  }, [hopPositions, flow.hops])

  useEffect(() => {
    if (!fitPoints.length || didFitRef.current) return
    controls.fitToData(fitPoints)
    didFitRef.current = true
  }, [fitPoints, controls.fitToData])

  return (
    <div className="panel" style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <PanelHeader
        openCount={openCount}
        onToggleIncidents={toggleWorldIncidents}
        isUpdating={dataDebouncing || streamsLoading || wsUiPaused || flow.loading}
        hasError={Boolean(lastDataError || flow.error)}
      />

      {/* Map body — no nested card/border; panel-head border is the only divider. */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 10, padding: 0 }}>
        {worldIncidentsOpen && (
          <div style={{ width: 190, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0, padding: '10px 0 10px 14px' }}>
            <IncidentsPanel incidents={derivedIncidents} onDrill={drillToRegion} card={card} col={col} />
          </div>
        )}

        <div style={{ flex: 1, minWidth: 0, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
          <ZoomControls onZoomIn={controls.zoomIn} onZoomOut={controls.zoomOut} onReset={controls.reset} />
          <Legend
            layers={worldLayers}
            clientGrain={worldClientGrain}
          />

          <svg
            ref={svgRef}
            viewBox={VIEW_BOX}
            preserveAspectRatio="xMidYMid meet"
            width="100%"
            height="100%"
            style={{
              display: 'block',
              height: '100%',
              width: '100%',
              touchAction: 'none',
              cursor: isPanning ? 'grabbing' : 'grab',
            }}
            onPointerDown={handlers.onPointerDown}
            onPointerMove={handlers.onPointerMove}
            onPointerUp={handlers.onPointerUp}
          >
            <style>{BASE_MAP_CSS}</style>
            {continentCss && <style>{continentCss}</style>}
            {countryCss && <style>{countryCss}</style>}

            <SvgGradients />

            <g transform={`translate(${pan.x} ${pan.y}) scale(${zoom})`}>
              <g className="world-land">
                <WorldPaths />
              </g>
              <MeridianLines zoom={zoom} />
              {worldLayers.flows && (
                <PipelineArcs
                  links={visibleLinks}
                  clusterArcs={continentClusterArcs}
                  positions={hopPositions}
                  hops={flow.hops}
                  zoom={zoom}
                  pulseEpoch={flow.epoch}
                  segment={worldSegment}
                  mapToScreen={mapToScreen}
                  onHoverEnter={onEnter}
                  onHoverLeave={onLeave}
                />
              )}
            </g>

            <g>
              <HopMarkers
                hops={flow.hops}
                positions={hopPositions}
                hiddenHopIds={clusteredHopIds}
                showStudios={worldLayers.studios}
                showClients={worldLayers.clients}
                showEdges={worldLayers.edges}
                pan={pan}
                zoom={zoom}
                pulseEpoch={flow.epoch}
                segment={worldSegment}
                clientGrain={worldClientGrain}
                onHoverEnter={onEnter}
                onHoverLeave={onLeave}
              />
              {worldLayers.studios && (
                <ClusterMarkers
                  clusters={studioClusters.clusters}
                  pulseEpoch={flow.epoch}
                  role="studio"
                  onHoverEnter={onEnter}
                  onHoverLeave={onLeave}
                />
              )}
              {worldLayers.clients && (
                <ClusterMarkers
                  clusters={clientClusters.clusters}
                  pulseEpoch={flow.epoch}
                  role="city"
                  onHoverEnter={onEnter}
                  onHoverLeave={onLeave}
                />
              )}
              {worldLayers.edges && (
                <ClusterMarkers
                  clusters={edgeClusters.clusters}
                  pulseEpoch={flow.epoch}
                  role="cdn_edge"
                  onHoverEnter={onEnter}
                  onHoverLeave={onLeave}
                />
              )}
              <Tooltip hover={hover} />
            </g>
          </svg>

          <TimezoneAxisOverlay
            svgRef={svgRef}
            mapToScreen={mapToScreen}
            zoom={zoom}
            pan={pan}
          />
        </div>
      </div>
    </div>
  )
}
